/* eslint-disable max-lines */
import { useAudioDevices } from '@hakimo-ui/hakimo/data-access';
import { AudioDevice, AudioFilterValues } from '@hakimo-ui/hakimo/types';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  eventTracker,
  trackFilters,
  useCanModifyAudioDevice,
  useCanModifyAudioDeviceCamMap,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { audioFilterAtom, filterConfigs, stateAtom } from '../state';
import AddAudioDeviceCamMapping from './add-audio-cam-mapping/AddAudioCamMapping';
import AddAudioDevice from './add-audio-device/AddAudioDevice';
import AudioExpandedRow from './table/AudioExpandedRow';
import { TableHeader } from './table/TableHeader';
import { columns, getTableData } from './table/util';
import { getCameraFilterParamsFromAudioFilters, getSearchParams } from './util';

export function AudioDeviceList() {
  const [state, setState] = useAtom(stateAtom);
  const { page, pageSize } = state;
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [audioFilters, setAudioFilters] = useAtom(audioFilterAtom);

  const [tableData, setTableData] = useState<TableData>({
    columns,
    rows: [],
  });

  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<
    AudioDevice | undefined
  >(undefined);
  const [openAddAudioCamMapping, setOpenAddAudioCamMapping] = useState(false);

  const [total, setTotal] = useState(0);
  const [openAddAudioDevice, setOpenAddAudioDevice] = useState(false);

  const cameraFilterParams = useMemo(() => {
    return getCameraFilterParamsFromAudioFilters(audioFilters);
  }, [audioFilters]);

  const { isLoading, isError, error, data } = useAudioDevices(
    getSearchParams(page, pageSize, audioFilters)
  );

  const fromIndex = (page - 1) * pageSize;
  const toIndex = Math.min(page * pageSize, total) - 1;

  const canAddAudioDevice = useCanModifyAudioDevice();
  const canModifyAudioCamMap = useCanModifyAudioDeviceCamMap();
  useEffect(() => {
    if (data) {
      const { items, total: t } = data;
      setTableData(
        getTableData(items, canModifyAudioCamMap, (audioDevice) => {
          setSelectedAudioDevice(audioDevice);
          setOpenAddAudioCamMapping(true);
        })
      );
      setAudioDevices(items);
      setTotal(t);
    }
  }, [data, canModifyAudioCamMap]);

  const onApplyFilters = (values: AudioFilterValues) => {
    setAudioFilters(values);
    trackFilters(values);
    setState((prev) => ({ ...prev, page: 1 }));
    setOpenFilterPanel(false);
  };

  const tableHeader = (
    <TableHeader
      isLoading={isLoading}
      hasCustomFiltersApplied={audioFilters.location.values.length > 0}
      onClickFilter={() => setOpenFilterPanel(true)}
    />
  );

  const tableFooter = (
    <div className="dark:bg-dark-bg border-t bg-white p-4 dark:border-gray-800">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={pageSize}
        total={total}
        disabled={isLoading}
        onChangePage={(val) =>
          setState((oldState) => {
            return { ...oldState, page: val };
          })
        }
      />
    </div>
  );

  const getExpandedRow = (rowId: string) => {
    const expandedAudioDevice = audioDevices.find(
      (audioDevice) => String(audioDevice.id) === rowId
    );
    return expandedAudioDevice ? (
      <AudioExpandedRow audioDevice={expandedAudioDevice} />
    ) : null;
  };

  return (
    <Page
      title="Audio Devices"
      titleFontSize="large"
      pageActions={[
        canAddAudioDevice ? (
          <Button
            variant="primary"
            onClick={() => setOpenAddAudioDevice(true)}
            disabled={isLoading}
            onSideEffect={() => eventTracker('open_add_audio_device_modal')}
          >
            Add Audio Device
            <span className="sr-only">Add Audio Device</span>
          </Button>
        ) : null,
      ]}
    >
      {isError && (
        <div className="mb-2">
          <Alert type="error">{error.message}</Alert>
        </div>
      )}
      {openAddAudioDevice && (
        <AddAudioDevice onClose={() => setOpenAddAudioDevice(false)} />
      )}
      {openAddAudioCamMapping && selectedAudioDevice && (
        <AddAudioDeviceCamMapping
          audioDevice={selectedAudioDevice}
          filterParams={cameraFilterParams}
          onClose={() => {
            setSelectedAudioDevice(undefined);
            setOpenAddAudioCamMapping(false);
          }}
        />
      )}
      <div className="dark:bg-dark-bg -mx-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden rounded-t-xl sm:-mx-6 md:mx-0">
        <Table
          data={tableData}
          header={tableHeader}
          footer={tableFooter}
          loading={isLoading}
          renderExpandedRow={getExpandedRow}
        />
      </div>
      <FilterPanel
        open={openFilterPanel}
        title="Audio Device Filters"
        filterConfigs={filterConfigs}
        initialFilterValues={audioFilters}
        trackingEventNameApply="apply_audio_filters"
        trackingEventNameReset="reset_audio_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={onApplyFilters}
      />
    </Page>
  );
}

export default withAuthz(withErrorBoundary(AudioDeviceList), [
  'audio_devices:view',
]);
